import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Checkbox, Popconfirm, message, Space, Tag } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useAccidents, useAccidentCreate, useAccidentUpdate, useAccidentDelete, useCCUsers } from "../../../../api-hooks/compliance";

const SEV_COLOR: Record<string, string> = { near_miss: "blue", minor: "orange", major: "red", fatal: "purple" };
const STATUS_COLOR: Record<string, string> = { open: "orange", under_review: "blue", closed: "green" };

export default function AccidentsTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);

  const { data: res, isLoading } = useAccidents(cid, { page, limit: 20 });
  const { data: users = [] } = useCCUsers(cid);
  const rows = (res as any)?.data ?? [];
  const total = (res as any)?.total ?? 0;

  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); setModal({ open: false, rec: null }); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useAccidentCreate(cid, cb("Accident report created."));
  const update = useAccidentUpdate(cid, cb("Updated."));
  const remove = useAccidentDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  function openEdit(r?: any) {
    setModal({ open: true, rec: r ?? null });
    form.setFieldsValue(r ? { ...r, incident_date: r.incident_date ? dayjs(r.incident_date) : null, riddor_reportable: !!r.riddor_reportable } : { severity: "minor", status: "open", riddor_reportable: false });
  }

  const columns = [
    { title: "Incident Date", dataIndex: "incident_date", key: "d", render: (v: string) => v ? dayjs(v).format("DD/MM/YY HH:mm") : "—" },
    { title: "Location", dataIndex: "location", key: "l", render: (v: string) => v || "—" },
    { title: "Severity", dataIndex: "severity", key: "sev", render: (v: string) => <Tag color={SEV_COLOR[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag> },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag> },
    { title: "RIDDOR", dataIndex: "riddor_reportable", key: "r", render: (v: number) => v ? <Tag color="red">RIDDOR</Tag> : "No" },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold text-slate-700 text-sm">Accident & Incident Reports</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openEdit()} style={{ background: "#4c1d95" }}>New Report</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={columns} loading={isLoading} size="small" pagination={{ current: page, pageSize: 20, total, onChange: setPage }} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit Accident Report" : "New Accident Report"} width={600}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, incident_date: vals.incident_date?.toISOString() };
          if (modal.rec) update.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="reported_by" label="Reported By (User ID)" rules={[{ required: true }]}><Input type="number" /></Form.Item>
            <Form.Item name="incident_date" label="Incident Date/Time" rules={[{ required: true }]}><DatePicker showTime className="w-full" format="DD/MM/YYYY HH:mm" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="severity" label="Severity" rules={[{ required: true }]}><Select options={["near_miss","minor","major","fatal"].map(v => ({ value: v, label: v.replace(/_/g," ").toUpperCase() }))} /></Form.Item>
            <Form.Item name="status" label="Status"><Select options={["open","under_review","closed"].map(v => ({ value: v, label: v.replace(/_/g," ").toUpperCase() }))} /></Form.Item>
          </div>
          <Form.Item name="location" label="Location"><Input /></Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}><Input.TextArea rows={3} /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="injury_type" label="Injury Type"><Input /></Form.Item>
            <Form.Item name="body_part_affected" label="Body Part"><Input /></Form.Item>
          </div>
          <Form.Item name="witnesses" label="Witnesses"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="immediate_action" label="Immediate Action"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="corrective_action" label="Corrective Action"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="riddor_reportable" valuePropName="checked"><Checkbox>RIDDOR Reportable</Checkbox></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || update.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
